// Selectores memoizados sin dependencias externas usando funciones puras

// Selectores base simples
export const getApplicationState = (state) => state?.applicationState;
export const getTemplateData = (state) => state?.applicationState?.templateData;
export const getHeader = (state) => state?.applicationState?.templateData?.header;
export const getFooter = (state) => state?.applicationState?.templateData?.footer;
export const getNavigation = (state) => state?.applicationState?.navigation;
export const getTemplateColors = (state) => state?.applicationState?.templateData?.colors;

// Selectores simples que devuelven primitivos (no causan re-renders)
export const selectAppName = (state) => getHeader(state)?.nombre || 'Aplicación';
export const selectAppId = (state) => getApplicationState(state)?.applicationId || '';

// Función auxiliar para comparación profunda simple
const shallowEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  
  if (keysA.length !== keysB.length) return false;
  
  for (let key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  
  return true;
};

// Cache simple para colores
let colorsCache = null;
let lastTemplateColors = null;

export const selectColors = (state) => {
  const templateColors = getTemplateColors(state);
  
  // Si los colores no han cambiado, devolver el cache
  if (templateColors === lastTemplateColors && colorsCache !== null) {
    return colorsCache;
  }
  
  try {
    if (!templateColors || templateColors.length === 0) {
      colorsCache = null;
      lastTemplateColors = templateColors;
      return null;
    }
    
    const parsed = JSON.parse(templateColors[0].valor);
    colorsCache = parsed;
    lastTemplateColors = templateColors;
    return parsed;
  } catch (error) {
    console.error('Error parsing colors:', error);
    colorsCache = null;
    lastTemplateColors = templateColors;
    return null;
  }
};

// Cache para objetos complejos
let headerCache = null;
let lastHeaderRef = null;

export const selectHeader = (state) => {
  const header = getHeader(state);
  
  if (header === lastHeaderRef && headerCache !== null) {
    return headerCache;
  }
  
  headerCache = header || {};
  lastHeaderRef = header;
  return headerCache;
};

let footerCache = null;
let lastFooterRef = null;

export const selectFooter = (state) => {
  const footer = getFooter(state);
  
  if (footer === lastFooterRef && footerCache !== null) {
    return footerCache;
  }
  
  footerCache = footer || {};
  lastFooterRef = footer;
  return footerCache;
};

let navigationCache = null;
let lastNavigationRef = null;

export const selectNavigation = (state) => {
  const navigation = getNavigation(state);
  
  if (navigation === lastNavigationRef && navigationCache !== null) {
    return navigationCache;
  }
  
  navigationCache = navigation || { actualModule: 'paquetes' };
  lastNavigationRef = navigation;
  return navigationCache;
};

// Cache para datos de la app
let appDataCache = null;
let lastAppDataInputs = null;

export const selectAppData = (state) => {
  const appName = selectAppName(state);
  const appId = selectAppId(state);
  const header = selectHeader(state);
  const colors = selectColors(state);
  const navigation = selectNavigation(state);
  const footer = selectFooter(state);
  const isLoading = !appId;
  
  const currentInputs = { appName, appId, header, colors, navigation, footer, isLoading };
  
  if (lastAppDataInputs && shallowEqual(currentInputs, lastAppDataInputs) && appDataCache !== null) {
    return appDataCache;
  }
  
  appDataCache = {
    appName,
    appId,
    header,
    colors,
    navigation,
    footer,
    isLoading,
    hasError: false
  };
  
  lastAppDataInputs = currentInputs;
  return appDataCache;
};

// Selectores para paquetes
export const getPackagesState = (state) => state?.packages;

let packagesCache = null;
let lastPackagesRef = null;

export const selectPackages = (state) => {
  const packages = getPackagesState(state)?.original;
  
  if (packages === lastPackagesRef && packagesCache !== null) {
    return packagesCache;
  }
  
  packagesCache = packages || [];
  lastPackagesRef = packages;
  return packagesCache;
};

let filteredPackagesCache = null;
let lastFilteredPackagesRef = null;

export const selectFilteredPackages = (state) => {
  const filteredPackages = getPackagesState(state)?.filtrados;
  
  if (filteredPackages === lastFilteredPackagesRef && filteredPackagesCache !== null) {
    return filteredPackagesCache;
  }
  
  filteredPackagesCache = filteredPackages || [];
  lastFilteredPackagesRef = filteredPackages;
  return filteredPackagesCache;
};

let packageDetailsCache = null;
let lastPackageDetailsRef = null;

export const selectPackageDetails = (state) => {
  const packageDetails = getPackagesState(state)?.details;
  
  if (packageDetails === lastPackageDetailsRef && packageDetailsCache !== null) {
    return packageDetailsCache;
  }
  
  packageDetailsCache = packageDetails || { isReady: false, data: null, paxQuantity: 0 };
  lastPackageDetailsRef = packageDetails;
  return packageDetailsCache;
};

let packagesDataCache = null;
let lastPackagesDataInputs = null;

export const selectPackagesData = (state) => {
  const packages = selectPackages(state);
  const filteredPackages = selectFilteredPackages(state);
  const packageDetails = selectPackageDetails(state);
  
  const currentInputs = { packages, filteredPackages, packageDetails };
  
  if (lastPackagesDataInputs && shallowEqual(currentInputs, lastPackagesDataInputs) && packagesDataCache !== null) {
    return packagesDataCache;
  }
  
  packagesDataCache = {
    packages,
    filteredPackages,
    packageDetails,
    isLoading: !packageDetails.isReady,
    hasError: false
  };
  
  lastPackagesDataInputs = currentInputs;
  return packagesDataCache;
};

// Selectores para banners
export const getBannersState = (state) => state?.bannersState;

// Similar caching pattern for banners...
let bannersDataCache = null;
let lastBannersState = null;

export const selectBannersData = (state) => {
  const bannersState = getBannersState(state);
  
  if (bannersState === lastBannersState && bannersDataCache !== null) {
    return bannersDataCache;
  }
  
  bannersDataCache = {
    banners: bannersState?.raw || [],
    mainCarousel: bannersState?.categoriesMainCarousel || [],
    categories: bannersState?.categories || [],
    isReady: bannersState?.ready || false,
    isLoading: !(bannersState?.ready || false),
    hasError: false
  };
  
  lastBannersState = bannersState;
  return bannersDataCache;
}; 